import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import SlackChannelPicker from "../SlackChannelPicker";
import InputWithVariablePicker from "../../components/InputWithVariablePicker";
import { getAppLink, connectApp } from "../../../api/AppLinkApi";
import { connectRedirectUrlAppIntegration } from "../../../Navigator";
import type { RuleItemEditorBaseProps } from "../ruleItemEditorTypes";
import { SlackSVG } from "../../../icons/SlackSVG";
import LayoutAction from "../../../actions/LayoutAction";

type Props = RuleItemEditorBaseProps & { data: any };

export default function RuleItemEditorSlack({ ruleItem, updateRuleItem, attributes, t }: Props) {
  const { projectId }: any = useParams();
  const dispatch = useDispatch();
  const [connectionStatus, setConnectionStatus] = useState<
    "loading" | "connected" | "disconnected"
  >("loading");
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check Slack connection status directly via getAppLink API
  useEffect(() => {
    getAppLink("slack")
      .then((appData: any) => {
        setConnectionStatus(appData?.connected ? "connected" : "disconnected");
      })
      .catch(() => {
        setConnectionStatus("disconnected");
      });
  }, []);

  const handleConnectSlack = async () => {
    setConnecting(true);
    setError(null);
    try {
      const postUrl = connectRedirectUrlAppIntegration(projectId, "slack");
      const result = await connectApp(projectId, "slack", postUrl);
      if (result?.redirectUrl) {
        // Open in new tab to avoid losing automation editor state
        window.open(result.redirectUrl, "_blank");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err?.message || "Failed to connect to Slack";
      setError(errorMessage);
      dispatch(
        LayoutAction.setNotification({
          title: t("Connect to Slack"),
          type: "error",
          message: errorMessage,
        })
      );
    } finally {
      setConnecting(false);
    }
  };

  const data = ruleItem.data || {};

  // Loading state
  if (connectionStatus === "loading") {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-400">
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        {t("Checking Slack connection...")}
      </div>
    );
  }

  if (connectionStatus === "disconnected") {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-5 rounded-lg border border-dashed border-gray-300 bg-gray-50">
        <SlackSVG className="w-10 h-10" />
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900 mb-1">
            {t("Slack is not connected")}
          </div>
          <div className="text-xs text-gray-500">
            {t("Connect your Slack workspace to send messages to channels")}
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-900 shadow-sm hover:bg-gray-50 active:bg-gray-100 transition-all cursor-pointer"
          onClick={handleConnectSlack}
          disabled={connecting}
        >
          <SlackSVG className="w-5 h-5" />
          <span>{connecting ? t("Connecting...") : t("Connect to Slack")}</span>
        </button>
        {error && (
          <div className="text-sm text-red-500 mt-2 text-center bg-red-50 p-2 rounded-md w-full border border-red-100">
            {error}
          </div>
        )}
      </div>
    );
  }

  // Connected state — show channel picker + message builder
  return (
    <div className="flex flex-col gap-5 w-full">
      {/* Channel Picker */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">{t("Channel")}</label>
        <SlackChannelPicker
          onChange={(channel) => {
            updateRuleItem({
              ...ruleItem,
              data: {
                ...ruleItem.data,
                channelId: channel.value,
                channelName: channel.label,
              },
            });
          }}
          channelName={data.channelName}
        />
      </div>

      {/* Message Builder */}
      <InputWithVariablePicker
        label={t("Message")}
        value={data.message ?? ""}
        onChange={(value) =>
          updateRuleItem({
            ...ruleItem,
            data: { ...ruleItem.data, message: value },
          })
        }
        placeholder={t("Type your message. Use {{ to add variables")}
        readOnly={false}
        attributes={attributes}
      />
    </div>
  );
}
